import { useRef, type KeyboardEvent, type PointerEvent } from 'react';

/** The calendar's share of the width beside the event card, in percent (owner remark 17: a smaller calendar by default). */
export const SPLIT_DEFAULT = 40;
export const SPLIT_MIN = 28;
export const SPLIT_MAX = 65;
const STEP = 2;

export const clampSplit = (n: number) => Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, n));
const splitKey = (userId: string) => `mek.events.split.${userId}`;

/** The person's last split in this browser; storage may be missing or blocked, then the default. */
export function loadSplit(userId: string | undefined): number {
  if (!userId) return SPLIT_DEFAULT;
  try {
    const raw = window.localStorage.getItem(splitKey(userId));
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) ? clampSplit(n) : SPLIT_DEFAULT;
  } catch {
    return SPLIT_DEFAULT;
  }
}
export function saveSplit(userId: string, value: number): void {
  try {
    window.localStorage.setItem(splitKey(userId), String(Math.round(value * 10) / 10));
  } catch {
    /* not remembered: the split still works for this visit */
  }
}

/**
 * The hairline between the calendar and the event card: drag it (the hit area is wider than the line) or use
 * ←/→ (Home / End to the ends). `value` is the calendar's share of the parent's width; `onChange` follows a drag,
 * `onCommit` gets the value to keep (end of a drag, every key press).
 */
export function PanelSplitter({ value, onChange, onCommit, controls }: { value: number; onChange: (v: number) => void; onCommit: (v: number) => void; controls: string }) {
  const drag = useRef<{ left: number; width: number; last: number } | null>(null);

  const at = (clientX: number) => {
    const d = drag.current!;
    return clampSplit(Math.round(((clientX - d.left) / d.width) * 1000) / 10);
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const box = e.currentTarget.parentElement?.getBoundingClientRect();
    if (!box || box.width <= 0) return;
    e.preventDefault();
    drag.current = { left: box.left, width: box.width, last: value };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.currentTarget.focus({ preventScroll: true });
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current.last = at(e.clientX);
    onChange(drag.current.last);
  };
  const end = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const last = at(e.clientX);
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    onCommit(last);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const next = { ArrowLeft: value - STEP, ArrowRight: value + STEP, Home: SPLIT_MIN, End: SPLIT_MAX }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    onCommit(clampSplit(next));
  };

  return (
    <div
      className="cal-split"
      role="separator"
      tabIndex={0}
      aria-label="Szerokość kalendarza"
      aria-orientation="vertical"
      aria-controls={controls}
      aria-valuenow={Math.round(value)}
      aria-valuemin={SPLIT_MIN}
      aria-valuemax={SPLIT_MAX}
      title="Przeciągnij, żeby zmienić szerokość kalendarza"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={onKeyDown}
    />
  );
}
